import * as React from 'react'

import { Box, Text, useClassNames } from '@factorialco/deprecated-design-system'
import includes from 'lodash/includes'

import useVirtualization from 'modules/core/hooks/useVirtualization'
import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

import Item, { GetUserExtraInformationFn } from './Item'
import * as styles from './index.vanilla.css'

const BlankSlate = () => {
  return (
    <Box padding="s40" alignItems="center">
      <Text.Normal>{i18n.t('common.employees_not_found')}</Text.Normal>
    </Box>
  )
}

const getUsersById = (userList: number[] | undefined, userId: number) => {
  if (!userList) return false

  return userList.includes(userId)
}

export type Size = 'small' | 'medium' | 'big' | 'full'

interface ListProps {
  users: User[]
  selectedIds: number[]
  onSelectUser: (user: User) => () => void
  isMultiSelect: boolean
  suggestedUsers?: number[]
  confirmedUsers?: number[]
  disabledUsers?: number[]
  onGetUserExtraInformation?: GetUserExtraInformationFn
  size?: Size
  virtualized?: boolean
}

export default function List({
  users,
  selectedIds,
  onSelectUser,
  isMultiSelect,
  suggestedUsers,
  confirmedUsers,
  disabledUsers,
  onGetUserExtraInformation,
  size = 'small',
  virtualized = false,
}: ListProps) {
  const cx = useClassNames(styles)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { visibleStartIndex, endIndex, topPlaceholderHeight, bottomPlaceholderHeight } =
    useVirtualization({
      items: users,
      defaultItemHeight: 48,
      containerRef,
    })

  if (!users.length) return <BlankSlate />

  const Content = ({ items }: { items: User[] }) => {
    return (
      <>
        {items.map((user) => (
          <Item
            key={user.attributes.id}
            isMultiSelect={isMultiSelect}
            onSelectUser={onSelectUser}
            selected={includes(selectedIds, user.attributes.id)}
            user={user}
            suggested={getUsersById(suggestedUsers, user.id)}
            confirmed={getUsersById(confirmedUsers, user.id)}
            disabled={getUsersById(disabledUsers, user.id)}
            onGetUserExtraInformation={onGetUserExtraInformation}
          />
        ))}
      </>
    )
  }

  if (!virtualized) return <Content items={users} />

  return (
    <div ref={containerRef} className={cx('virtualizedList', size)}>
      <div style={{ height: `${topPlaceholderHeight}px` }}></div>
      <Content items={users.slice(visibleStartIndex, endIndex)} />
      <div style={{ height: `${bottomPlaceholderHeight}px` }}></div>
    </div>
  )
}
